import 'css/src/luminary/luminary-scene-image.module.scss';

import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import type Luminary from '@lumen5/luminary';
import type { SceneImageRenderMode } from '@lumen5/luminary/enums';

import type { LuminarySceneImageRenderingObserver } from './luminary-scene-image-rendering-observer';

import { ErrorReporter } from 'js/src/libs/error-reporter';
import { sentryCanvasSnapshot } from 'js/src/libs/sentry';
import uuid from 'js/src/libs/uuid';
import { useLuminaryContext } from 'js/src/luminary/luminary-context-provider';
import { useLuminaryImmediateRender } from 'js/src/luminary/luminary-hooks';

type LuminarySceneImagePresentationProps = {
    luminary: Luminary;
    sceneId: string;
    subsceneIndex: number;
    /**
     * If true, progressive rendering will be used
     * As soon as we have the data for asset, we will draw scene containing that asset
     * Which means that the image will be drawn in multiple passes
     * This is more interactive, but less performant
     */
    drawProgressive: boolean;
    renderingObserver?: LuminarySceneImageRenderingObserver;
    renderingMode?: SceneImageRenderMode;
    /**
     * When set, render the composited frame at this luminary time (in ms) instead of the scene's
     * own static preview. Unlike a single-scene preview, this includes whatever else is on screen
     * at that time — e.g. the blend between two scenes during a transition.
     */
    renderTime?: number;
    className?: string;
    ref?: React.Ref<HTMLCanvasElement>;
};

/**
 * Component automatically updates preview of a scene
 * It seeks to the middle of the scene, or subscene
 */
function LuminarySceneImagePresentation({
    luminary,
    sceneId,
    subsceneIndex,
    drawProgressive,
    renderingObserver,
    renderingMode,
    renderTime,
    className,
    ref: canvasRefExternal,
}: LuminarySceneImagePresentationProps) {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    useImperativeHandle<HTMLCanvasElement | null, HTMLCanvasElement | null>(
        canvasRefExternal,
        () => canvasRef.current,
    );

    const render = useCallback(async () => {
        if (!canvasRef.current) {
            return;
        }

        const scene = luminary.getSceneById(sceneId);

        if (!scene) {
            return;
        }

        const { width, height } = luminary.outputResolution;

        // prevents canvas from being cleared
        // even if the size is the same as before, when we set canvas size it updates it's buffer
        if (canvasRef.current.width !== width || canvasRef.current.height !== height) {
            canvasRef.current.width = width;
            canvasRef.current.height = height;
        }

        if (renderTime != null) {
            // renderAtTimeToCanvas throws while the ticker is running, so skip while running
            if (luminary.timeline.tickerRunning) {
                return;
            }
            await luminary.renderAtTimeToCanvas(renderTime, canvasRef.current);
        } else {
            await scene.renderPreviewToCanvas(canvasRef.current, {
                subsceneIndex,
                mode: renderingMode,
            });
        }
        await sentryCanvasSnapshot(canvasRef.current);
    }, [luminary, sceneId, subsceneIndex, renderingMode, renderTime]);

    const [renderId] = useState(() => uuid());

    const requestRendering = useCallback(async () => {
        renderingObserver?.onRenderingStart(renderId);
        try {
            await render();
        } catch (error) {
            // A failed render leaves this one canvas blank, which is recoverable. Swallowing it
            // here keeps a scene luminary can't draw from stalling observers that are waiting on
            // the render to finish.
            ErrorReporter.capture(error, { level: 'warning' });
        } finally {
            renderingObserver?.onRenderingFinish(renderId);
        }
    }, [render, renderingObserver, renderId]);

    const shouldRender = useCallback(
        ({ sceneId: sId, subsceneIndex: sIndex }: { sceneId: string; subsceneIndex: number }) => {
            if (sId !== sceneId || sIndex !== subsceneIndex) {
                return false;
            }
            const scene = luminary.getSceneById(sceneId);
            if (!scene || (!drawProgressive && !scene.areAssetsDoneTryingToLoad)) {
                return false;
            }
            return true;
        },
        [luminary, sceneId, subsceneIndex, drawProgressive],
    );

    useLuminaryImmediateRender<{ sceneId: string; subsceneIndex: number }>({
        luminary,
        render: requestRendering,
        shouldRender,
    });

    useEffect(() => {
        return () => {
            renderingObserver?.onImageDestroy(renderId);
        };
    }, [renderingObserver, renderId]);

    return (
        <canvas
            ref={canvasRef}
            role="img"
            aria-label="scene preview"
            className={className}
            data-luminary-name={luminary.name}
        />
    );
}

type LuminarySceneImageProps = {
    sceneId: string;
    subsceneIndex?: number;
    drawProgressive?: boolean;
    renderingObserver?: LuminarySceneImageRenderingObserver;
    renderingMode?: SceneImageRenderMode;
    renderTime?: number;
    className?: string;
    ref?: React.Ref<HTMLCanvasElement>;
};
export function LuminarySceneImage({
    sceneId,
    subsceneIndex = 0,
    drawProgressive = true,
    renderingObserver,
    renderingMode,
    renderTime,
    className,
    ref,
}: LuminarySceneImageProps) {
    const luminary = useLuminaryContext();
    return (
        <LuminarySceneImagePresentation
            luminary={luminary}
            sceneId={sceneId}
            subsceneIndex={subsceneIndex}
            drawProgressive={drawProgressive}
            renderingObserver={renderingObserver}
            renderingMode={renderingMode}
            renderTime={renderTime}
            className={className}
            ref={ref}
        />
    );
}
